import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, Phone, ArrowRight, AlertTriangle, Wind, Home, Trees } from 'lucide-react';
import { Button } from '../components/ui/button';
import { locations } from './LocationPage';

const FEATURED_AREAS = [
  {
    slug: 'victoria', name: 'Victoria', icon: AlertTriangle, color: 'text-amber-400', borderColor: 'border-amber-500/20',
    tagline: 'Aging Infrastructure Specialists',
    description: 'Heritage homes with knob-and-tube wiring, ungrounded outlets, and maxed-out panels. We modernize Victoria\u2019s oldest electrical systems safely.',
    services: [
      { label: 'EV Chargers', url: '/ev-charger-installation-victoria' },
      { label: 'Panel Upgrades', url: '/panel-upgrades-victoria' },
      { label: 'EPR Inspections', url: '/epr-inspection-victoria' },
      { label: 'Generators', url: '/generator-installation-victoria' },
    ],
  },
  {
    slug: 'langford', name: 'Langford', icon: Home, color: 'text-blue-400', borderColor: 'border-blue-500/20',
    tagline: 'New Construction Quality Experts',
    description: 'Fast-built homes with builder-grade electrical. We fix the shortcuts \u2014 maxed panels, minimum outlets, and missing future-proofing.',
    services: [
      { label: 'EV Chargers', url: '/ev-charger-installation-langford' },
      { label: 'Panel Upgrades', url: '/panel-upgrades-langford' },
      { label: 'Lighting', url: '/lighting-installation-langford' },
      { label: 'Residential', url: '/residential-electrician-langford' },
    ],
  },
  {
    slug: 'sooke', name: 'Sooke', icon: Wind, color: 'text-cyan-400', borderColor: 'border-cyan-500/20',
    tagline: 'Storm-Ready Electrical Solutions',
    description: 'Juan de Fuca windstorms mean 5\u201310 outages per year. Generators, surge protection, and backup power to keep you running.',
    services: [
      { label: 'Generators', url: '/generator-installation-sooke' },
      { label: 'Panel Upgrades', url: '/panel-upgrades-sooke' },
      { label: 'Residential', url: '/residential-electrician-sooke' },
      { label: 'Lighting', url: '/lighting-installation-sooke' },
    ],
  },
  {
    slug: 'duncan', name: 'Duncan / Cowichan Valley', icon: Trees, color: 'text-green-400', borderColor: 'border-green-500/20',
    tagline: 'Vacation Property Protection',
    description: 'Lake cabins and seasonal homes need smart monitoring, winterizing, and protection while you\u2019re away. We keep your property safe remotely.',
    services: [
      { label: 'Residential', url: '/residential-electrician-duncan' },
      { label: 'Panel Upgrades', url: '/panel-upgrades-duncan' },
      { label: 'Generators', url: '/generator-installation-duncan' },
      { label: 'Lighting', url: '/lighting-installation-duncan' },
    ],
  },
];

const AreasPage = () => {
  // Group locations by region
  const regions = {};
  Object.entries(locations).forEach(([slug, location]) => {
    if (!regions[location.region]) {
      regions[location.region] = [];
    }
    regions[location.region].push({ slug, ...location });
  });

  // Order regions geographically
  const regionOrder = [
    'Greater Victoria',
    'West Shore', 
    'Saanich Peninsula',
    'Cowichan Valley',
    'Central Vancouver Island',
    'West Coast',
    'Comox Valley',
    'North Vancouver Island',
    'Island-Wide'
  ];

  const areasTitle = 'Service Areas | Electrician Vancouver Island | Island Coastal Electric';
  const areasDescription = 'Island Coastal Electric serves all of Vancouver Island. From Victoria to Port Hardy, we provide professional electrical services to every community. Find your area!';

  useEffect(() => {
    document.title = areasTitle;
    const setMeta = (name, content, property) => {
      const attr = property ? 'property' : 'name';
      let el = document.querySelector('meta[' + attr + '="' + name + '"]');
      if (!el) { el = document.createElement('meta'); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };
    setMeta('description', areasDescription);
    setMeta('keywords', 'electrician Vancouver Island, Victoria electrician, Nanaimo electrician, Duncan electrician, Courtenay electrician, Campbell River electrician, Langford electrician, Sooke electrician');
    setMeta('og:title', areasTitle, true);
    setMeta('og:description', areasDescription, true);
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement('link'); canonical.setAttribute('rel', 'canonical'); document.head.appendChild(canonical); }
    canonical.setAttribute('href', 'https://islandcoastalelectric.ca/areas');
  }, [areasTitle, areasDescription]);

  return (
    <>

      <div className="min-h-screen bg-primary-dark pt-28 lg:pt-32">
        {/* Hero */}
        <section className="py-16 md:py-24 bg-secondary-dark">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center"
            >
              <div className="flex items-center justify-center gap-2 text-electric-teal mb-4">
                <MapPin className="w-5 h-5" />
                <span className="font-bold uppercase tracking-wider text-sm">Service Areas</span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white tracking-tight mb-6">
                Serving All of Vancouver Island
              </h1>
              <p className="text-white/70 text-lg md:text-xl max-w-3xl mx-auto mb-8">
                From Victoria to Port Hardy, Tofino to Campbell River — Island Coastal Electric brings professional electrical services to every community on Vancouver Island.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Button asChild size="lg" className="bg-electric-teal text-primary-dark font-bold">
                  <Link to="/book-estimate">
                    Get Free Estimate <ArrowRight className="ml-2 w-5 h-5" />
                  </Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="border-white/30 text-white">
                  <a href="tel:778-960-2377">
                    <Phone className="mr-2 w-5 h-5" /> 778-960-2377
                  </a>
                </Button>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-12 border-b border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              <div>
                <p className="text-electric-teal text-4xl font-bold">{Object.keys(locations).length}</p>
                <p className="text-white/60">Communities Served</p>
              </div>
              <div>
                <p className="text-electric-teal text-4xl font-bold">870K+</p>
                <p className="text-white/60">Island Population</p>
              </div>
              <div>
                <p className="text-electric-teal text-4xl font-bold">460km</p>
                <p className="text-white/60">Island Length</p>
              </div>
              <div>
                <p className="text-electric-teal text-4xl font-bold">100%</p>
                <p className="text-white/60">Coverage</p>
              </div>
            </div>
          </div>
        </section>

        {/* Featured Service Areas */}
        <section className="py-16 md:py-20 bg-secondary-dark" data-testid="featured-areas">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <span className="text-electric-teal font-bold uppercase tracking-widest text-sm">Most Popular Areas</span>
              <h2 className="text-3xl md:text-4xl font-bold text-white mt-3">Featured Service Areas</h2>
              <p className="text-white/40 mt-3 max-w-2xl mx-auto">Each area has unique electrical challenges. We tailor our approach to what your community actually needs.</p>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {FEATURED_AREAS.map((area, i) => (
                <motion.div key={area.slug} initial={{opacity:0,y:15}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{delay:i*0.1}}
                  className={`bg-primary-dark rounded-xl p-6 md:p-8 border ${area.borderColor} hover:border-opacity-60 transition-colors`}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <area.icon className={`w-5 h-5 ${area.color}`} />
                    <h3 className="text-white font-bold text-xl">{area.name}</h3>
                  </div>
                  <p className={`${area.color} text-sm font-semibold mb-2`}>{area.tagline}</p>
                  <p className="text-white/50 text-sm leading-relaxed mb-5">{area.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {area.services.map((svc, j) => (
                      <Link key={j} to={svc.url}
                        className="bg-white/5 border border-white/10 rounded-full px-3 py-1.5 text-xs text-white/70 hover:text-electric-teal hover:border-electric-teal/30 transition-colors"
                      >
                        {svc.label} &rarr;
                      </Link>
                    ))}
                    <Link to={`/areas/${area.slug}`}
                      className="bg-electric-teal/10 border border-electric-teal/20 rounded-full px-3 py-1.5 text-xs text-electric-teal hover:bg-electric-teal/20 transition-colors font-semibold"
                    >
                      All {area.name} Services &rarr;
                    </Link>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Locations by Region */}
        <section className="py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {regionOrder.map((regionName) => {
              const regionLocations = regions[regionName];
              if (!regionLocations) return null;
              
              return (
                <div key={regionName} className="mb-16 last:mb-0">
                  <h2 className="text-2xl md:text-3xl font-bold text-white mb-6 flex items-center gap-3">
                    <MapPin className="w-6 h-6 text-electric-teal" />
                    {regionName}
                  </h2>
                  <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {regionLocations.map((location) => (
                      <Link
                        key={location.slug}
                        to={`/areas/${location.slug}`}
                        className="bg-secondary-dark p-5 rounded-lg border border-white/10 hover:border-electric-teal/50 transition-all group"
                      >
                        <h3 className="text-white font-bold text-lg group-hover:text-electric-teal transition-colors">
                          {location.name}
                        </h3>
                        <p className="text-white/50 text-sm mt-1">{location.driveTime} from Victoria</p>
                        <p className="text-electric-teal text-sm mt-3 opacity-0 group-hover:opacity-100 transition-opacity">
                          View services →
                        </p>
                      </Link>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 md:py-24 bg-secondary-dark">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Don't See Your Area?
            </h2>
            <p className="text-white/70 text-lg mb-8">
              We serve ALL of Vancouver Island. If your community isn't listed, we still come to you! Contact us and we'll provide service to your location.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button asChild size="lg" className="bg-electric-teal text-primary-dark font-bold">
                <Link to="/contact">Contact Us</Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-white/30 text-white">
                <a href="tel:778-960-2377">
                  <Phone className="mr-2 w-5 h-5" /> 778-960-2377
                </a>
              </Button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default AreasPage;
